import React, { useState, useMemo, useEffect } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { getWeekStartDate, formatWeekLabel, getWeekNumber } from '../utils/helpers.ts';
import { getWeekDateRange } from '../utils/timeUtils.js';

// Navigation icons
const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

const WeekSelectionModal = ({ isOpen, onClose, currentDate, onWeekSelect }) => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedWeekNumber, setSelectedWeekNumber] = useState(
        currentDate ? getWeekNumber(currentDate) : getWeekNumber(new Date())
    );

    // Update selected values when currentDate changes
    useEffect(() => {
        if (currentDate) {
            setSelectedYear(currentDate.getFullYear());
            setSelectedWeekNumber(getWeekNumber(currentDate));
        }
    }, [currentDate]);

    // Generate weeks for the selected year
    const weeksInYear = useMemo(() => {
        const weeks = [];
        for (let weekNum = 1; weekNum <= 53; weekNum++) {
            const weekRange = getWeekDateRange(selectedYear, weekNum);
            if (weekRange) {
                const { startDate, endDate } = weekRange;
                // Check if this week actually belongs to the selected year
                if (startDate.getFullYear() === selectedYear || endDate.getFullYear() === selectedYear) {
                    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
                    const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
                    const startDay = startDate.getDate();
                    const endDay = endDate.getDate();

                    // Create a concise date range display
                    let dateRange;
                    if (startMonth === endMonth) {
                        dateRange = `${startMonth} ${startDay}-${endDay}`;
                    } else {
                        dateRange = `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
                    }

                    weeks.push({
                        weekNumber: weekNum,
                        startDate,
                        endDate,
                        label: formatWeekLabel(startDate),
                        dateRange
                    });
                }
            }
        }
        return weeks;
    }, [selectedYear]);

    const handleYearChange = (direction) => {
        setSelectedYear(prev => prev + direction);
        // Reset week selection when changing year
        setSelectedWeekNumber(1);
    };

    const handleWeekSelect = (weekNumber) => {
        setSelectedWeekNumber(weekNumber);
    };

    const handleConfirm = () => {
        const weekRange = getWeekDateRange(selectedYear, selectedWeekNumber);
        if (weekRange && onWeekSelect) {
            onWeekSelect(weekRange.startDate);
        }
        onClose();
    };

    const handleGoToCurrentWeek = () => {
        const now = new Date();
        setSelectedYear(now.getFullYear());
        setSelectedWeekNumber(getWeekNumber(now));
    };

    if (!isOpen) return null;

    const selectedWeek = weeksInYear.find(w => w.weekNumber === selectedWeekNumber);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Select Week">
            <div className="space-y-3">
                {/* Year Navigation */}
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => handleYearChange(-1)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Previous Year"
                    >
                        <ChevronLeftIcon />
                    </button>
                    <h3 className="text-lg font-semibold">{selectedYear}</h3>
                    <button
                        onClick={() => handleYearChange(1)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Next Year"
                    >
                        <ChevronRightIcon />
                    </button>
                </div>

                {/* Quick Actions */}
                <div className="flex justify-center">
                    <button
                        onClick={handleGoToCurrentWeek}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                        Go to current week
                    </button>
                </div>

                {/* Week Grid - Compact */}
                <div className="max-h-60 overflow-y-auto border rounded p-2">
                    <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-1">
                        {weeksInYear.map(week => {
                            const isSelected = week.weekNumber === selectedWeekNumber;
                            const isCurrentWeek = currentDate &&
                                getWeekNumber(currentDate) === week.weekNumber &&
                                currentDate.getFullYear() === selectedYear;

                            return (
                                <button
                                    key={week.weekNumber}
                                    onClick={() => handleWeekSelect(week.weekNumber)}
                                    className={`
                                         px-1 py-2 text-xs rounded transition-colors duration-150 text-center min-w-0 flex flex-col
                                         ${isSelected
                                            ? 'bg-blue-600 text-white font-semibold'
                                            : isCurrentWeek
                                                ? 'bg-blue-100 text-blue-800 font-medium'
                                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                        }
                                         focus:outline-none focus:ring-1 focus:ring-blue-500
                                     `}
                                    title={`Week ${week.weekNumber}: ${week.dateRange}`}
                                >
                                    <div className="font-medium leading-tight">{week.weekNumber}</div>
                                    <div className="text-xs opacity-75 leading-tight">
                                        {week.startDate.getDate()} {week.startDate.toLocaleDateString('en-US', { month: 'short' })}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Selected Week Info - Compact */}
                {selectedWeek && (
                    <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded text-center">
                        <span className="font-medium">Week {selectedWeekNumber}</span>
                        <span className="mx-2">•</span>
                        <span>{selectedWeek.dateRange}</span>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-2 pt-2 border-t">
                    <Button variant="secondary" onClick={onClose} className="text-sm px-3 py-1.5">
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirm} className="text-sm px-3 py-1.5">
                        Select
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default WeekSelectionModal; 